// src/pages/product-module/ReadProducts.js

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import DashboardWrapper from "../../components/DashboardWrapper";
import CrudTable from "../../components/dashboard/table/CrudTable";
import apiService from "../../services/apiService";
import productService from "../../services/productService";
import { useProductsCrud } from "../../hooks/useProductsCrud";

const localCacheKey = "productReadCache";

const generateSlug = (text) =>
  text.toString().toLowerCase().trim().replace(/\s+/g, "-");

export default function SellerPendingReadProducts() {
  const navigate = useNavigate();

  const { deleteProduct } = useProductsCrud();
const [searchText, setSearchText] = useState("");
  const [cachedData, setCachedData] = useState(() => {
    const cache = localStorage.getItem(localCacheKey);
    return cache ? JSON.parse(cache) : null;
  });

  const [localData, setLocalData] = useState(cachedData?.products || []);
  const [loading, setLoading] = useState(!cachedData);
  const [selectedRows, setSelectedRows] = useState([]);

  const showSwal = useCallback((title, text, icon) => {
    Swal.fire({
      title,
      text,
      icon,
      confirmButtonText: "OK",
      customClass: { confirmButton: "btn btn-primary" },
      buttonsStyling: false,
    });
  }, []);

 
const enrichProducts = useCallback((products, categories, subCategories) => {
  const productList = Array.isArray(products) ? products : [];
  const categoryList = Array.isArray(categories) ? categories : [];
  const subCategoryList = Array.isArray(subCategories)
    ? subCategories
    : [];

  return productList.map((product, index) => {
    const category = categoryList.find(
      (cat) => String(cat.id) === String(product.category_id)
    );

    const subCategory = subCategoryList.find(
      (sub) => String(sub.id) === String(product.sub_category_id)
    );

    const variant = Array.isArray(product.variant_items)
      ? product.variant_items[0] || {}
      : {};

    return {
      ...product,

      thumb_image:
        product.thumb_image_url ||
        product.thumb_image ||
        "",

      price:
        variant.variant_offer_price ??
        variant.variant_price ??
        product.price ??
        "",

      sku:
        variant.variant_sku ??
        product.sku ??
        "",

      qty:
        variant.variant_qty ??
        product.qty ??
        "",

      category_name: category?.name || "Unknown",
      sub_category_name: subCategory?.name || "Unknown",
      sl: index + 1,
    };
  });
}, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [productRes, categoryRes, subCategoryRes] = await Promise.all([
        productService.getUnapprovedProducts(),
        apiService.getAll("backend/categories"),
        apiService.getAll("backend/sub-categories"),
      ]);

  
const getArray = (response) => {
  const body = response?.data ?? response;

  if (Array.isArray(body)) return body;

  if (Array.isArray(body?.data)) return body.data;

  if (Array.isArray(body?.data?.data)) return body.data.data;

  if (Array.isArray(body?.categories)) return body.categories;

  if (Array.isArray(body?.sub_categories)) return body.sub_categories;

  return [];
};

const products = getArray(productRes);
const categories = getArray(categoryRes);
const subCategories = getArray(subCategoryRes);

      const enriched = enrichProducts(products, categories, subCategories);

      const newCache = {
        products: enriched,
        categories,
        subCategories,
      };

      const existingCache = localStorage.getItem(localCacheKey);
      const parsedCache = existingCache ? JSON.parse(existingCache) : null;

      if (
        !parsedCache ||
        JSON.stringify(parsedCache.products) !== JSON.stringify(enriched) ||
        JSON.stringify(parsedCache.categories) !== JSON.stringify(categories) ||
        JSON.stringify(parsedCache.subCategories) !== JSON.stringify(subCategories)
      ) {
        localStorage.setItem(localCacheKey, JSON.stringify(newCache));
        setCachedData(newCache);
      }

      setLocalData(enriched);
    } catch (error) {
      console.error("Failed to load data:", error);
      showSwal("Error", "Failed to load product data.", "error");
    } finally {
      setLoading(false);
    }
  }, [enrichProducts, showSwal]);

  useEffect(() => {
    if (cachedData) {
      setLocalData(cachedData.products);
      setLoading(false);
      loadData(); // Refresh in background
    } else {
      loadData();
    }
  }, [cachedData, loadData]);

  const updateLocalDataCache = (updatedProducts) => {
    if (!cachedData) return;
    const enriched = enrichProducts(updatedProducts, cachedData.categories, cachedData.subCategories);
    const newCache = {
      products: enriched,
      categories: cachedData.categories,
      subCategories: cachedData.subCategories,
    };
    setLocalData(enriched);
    setCachedData(newCache);
    localStorage.setItem(localCacheKey, JSON.stringify(newCache));
  };

  const handleDelete = async (id) => {
      const confirm = await Swal.fire({
    title: "Are you sure?",
    text: "This action cannot be undone!",
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: "Yes, delete it!",
    cancelButtonText: "Cancel",
    confirmButtonColor: "#3b82f6",
    cancelButtonColor: "#6c757d", 
    reverseButtons: true, 
  });

    if (confirm.isConfirmed) {
      try {
        await deleteProduct(id);
        const updated = localData.filter((item) => item.id !== id);
        updateLocalDataCache(updated);
        showSwal("Deleted!", "Product has been deleted.", "success");
      } catch (error) {
        console.error("Delete error:", error.response?.data || error);
        showSwal("Error", error.response?.data?.message || "Failed to delete.", "error");
      }
    }
  };

  // ✅ Use changeProductStatus API
  const handleToggleStatus = useCallback(
    async (item) => {
      try {
        await productService.changeProductStatus(item.id, !item.status);

        const updatedProducts = localData.map((product) =>
          product.id === item.id ? { ...product, status: !item.status } : product
        );

        updateLocalDataCache(updatedProducts);
        showSwal("Success!", "Product status updated.", "success");
      } catch (error) {
        console.error("Status update failed:", error);
        showSwal("Error!", "Failed to update product status.", "error");
      }
    },
    [localData, updateLocalDataCache, showSwal]
  );
const handleSelectRow = (id) => {
  setSelectedRows((prev) =>
    prev.includes(id)
      ? prev.filter((item) => item !== id)
      : [...prev, id]
  );
};

const handleSelectAll = () => {
  if (selectedRows.length === localData.length) {
    setSelectedRows([]);
  } else {
    setSelectedRows(localData.map((item) => item.id));
  }
};

const handleBulkDelete = async () => {
  if (selectedRows.length === 0) {
    showSwal("Warning", "Please select products.", "warning");
    return;
  }

  const confirm = await Swal.fire({
    title: "Delete Selected Products?",
    text: `You are deleting ${selectedRows.length} products.`,
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: "Yes Delete",
    cancelButtonText: "Cancel",
    confirmButtonColor: "#3b82f6",
  });

  if (!confirm.isConfirmed) return;

  try {
    await productService.bulkDeleteProducts(selectedRows);

    const updated = localData.filter(
      (item) => !selectedRows.includes(item.id)
    );

    updateLocalDataCache(updated);

    setSelectedRows([]);

    showSwal(
      "Success",
      "Products deleted successfully.",
      "success"
    );
  } catch (error) {
    console.error(error);

    showSwal(
      "Error",
      error.response?.data?.message || "Bulk delete failed",
      "error"
    );
  }
};

const handleBulkApproval = async (status) => {
  if (selectedRows.length === 0) {
    showSwal("Warning", "Please select products.", "warning");
    return;
  }

  const confirm = await Swal.fire({
    title: status
      ? "Approve Selected Products?"
      : "Unapprove Selected Products?",
    text: `You selected ${selectedRows.length} products.`,
    icon: "question",
    showCancelButton: true,
    confirmButtonText: status ? "Approve" : "Unapprove",
    cancelButtonText: "Cancel",
    confirmButtonColor: status ? "#198754" : "#dc3545",
  });

  if (!confirm.isConfirmed) return;

  try {
    await productService.bulkApproveProducts(
      selectedRows,
      status
    );

    const updatedProducts = localData.map((product) =>
      selectedRows.includes(product.id)
        ? {
            ...product,
            is_approved: status,
          }
        : product
    );

    updateLocalDataCache(updatedProducts);

    setSelectedRows([]);

    showSwal(
      "Success",
      status
        ? "Products approved successfully."
        : "Products unapproved successfully.",
      "success"
    );
  } catch (error) {
    console.error(error);

    showSwal(
      "Error",
      error.response?.data?.message ||
        "Bulk approval failed",
      "error"
    );
  }
};
  // ✅ Use changeProductApprovalStatus API
  const handleToggleApprovedStatus = async (item, newStatus) => {
    try {
      const isApproved = newStatus === "approved";
      await productService.changeProductApprovalStatus(item.id, isApproved);

      const updatedProducts = localData.map((product) =>
        product.id === item.id ? { ...product, is_approved: isApproved } : product
      );

      updateLocalDataCache(updatedProducts);
      showSwal("Success", "Approval status updated.", "success");
    } catch (error) {
      console.error("Approval update error:", error);
      showSwal("Error", "Failed to update approval status.", "error");
    }
  };

  return (
    <DashboardWrapper>
      <div className="container-fluid mt-4">

        <div className="d-flex justify-content-between align-items-center mb-4">
          <button
            className="btn btn-secondary d-flex align-items-center justify-content-between"
            onClick={() => navigate(-1)}
          >
            <span>Back</span>
          </button>
          
        </div>

          
        <div className="card shadow">
         <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-3">
    <h4 className="mb-0">Seller Inactive Products</h4>

  <input
    type="text"
    className="form-control"
    placeholder="Search products..."
    value={searchText}
    onChange={(e) => setSearchText(e.target.value)}
    style={{
      width: "260px",
      maxWidth: "100%",
      borderRadius: "6px",
    }}
  />
</div>

          <div className="card-body">

<div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
  {/* Left side: Bulk actions */}
  <div className="d-flex flex-wrap align-items-center gap-2">
    <button
      className="btn"
      onClick={handleSelectAll}
      style={{
        backgroundColor: "#3b82f6",
        borderColor: "#3b82f6",
        color: "#fff",
      }}
    >
      {selectedRows.length === localData.length
        ? "Unselect All"
        : "Select All"}
    </button>

    <button
      className="btn btn-success"
      onClick={() => handleBulkApproval(true)}
      disabled={selectedRows.length === 0}
    >
      Approve Selected ({selectedRows.length})
    </button>

    <button
      className="btn btn-warning text-dark"
      onClick={() => handleBulkApproval(false)}
      disabled={selectedRows.length === 0}
    >
      Unapprove Selected
    </button>

    <button
      className="btn btn-danger"
      onClick={handleBulkDelete}
      disabled={selectedRows.length === 0}
    >
      Delete Selected
    </button>
  </div>

  {/* Right side: Export buttons */}
  <div className="d-flex flex-wrap align-items-center gap-2">
    <button
      type="button"
      className="btn btn-success"
      onClick={() => window.dispatchEvent(new Event("download-products-excel"))}
    >
      <i className="bx bx-spreadsheet me-1"></i>
      Download Excel
    </button>

    <button
      type="button"
      className="btn btn-danger"
      onClick={() => window.dispatchEvent(new Event("download-products-pdf"))}
    >
      <i className="bx bxs-file-pdf me-1"></i>
      Download PDF
    </button>
  </div>
</div>

<div className="mt-3 mb-3">
  <small className="text-muted fw-semibold">
    {selectedRows.length > 0
      ? `${selectedRows.length} item${
          selectedRows.length > 1 ? "s" : ""
        } selected`
      : "No items selected"}
  </small>
</div>
          {loading ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ minHeight: "200px" }}
            >
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <CrudTable
            
              data={localData}
                selectedRows={selectedRows}
  onSelectRow={handleSelectRow}
                               onEditLink={(row) => `/admin/products/edit/${row.id}`}

              onDelete={handleDelete}
              onToggleStatus={handleToggleStatus}
              onToggleApprovedStatus={handleToggleApprovedStatus} 
                searchText={searchText}
              columns={[
                "sl",
                "thumb_image",
                "name",
                "price",
                "sku",
                "qty",
                "status",
                "is_approved",
              ]}
            />
          )}
        </div>
      </div>
      </div>
    </DashboardWrapper>
  );
}
